import { X, AlertTriangle, Info, Sparkles, Skull, Flame } from 'lucide-react';
import { getEventEffectEntries } from './eventEffects';

interface RandomEvent {
  eventId: string;
  day: number;
  name: string;
  severity: 'minor' | 'major' | 'critical';
  message: string;
  visualMarker: string;
  resolved: boolean;
  details?: Record<string, unknown>;
  effects?: Record<string, unknown>;
  effectDeltas?: {
    stats?: Record<string, unknown>;
    environmental?: Record<string, unknown>;
  };
}

interface EventLogPanelProps {
  events: RandomEvent[];
  currentDay: number;
  onClose: () => void;
}

const SEVERITY_CONFIG = {
  minor: { icon: Sparkles, color: 'text-blue-400', bg: 'bg-blue-900/30', border: 'border-blue-700/40' },
  major: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-900/30', border: 'border-amber-700/40' },
  critical: { icon: Skull, color: 'text-red-400', bg: 'bg-red-900/30', border: 'border-red-700/40' },
};

export default function EventLogPanel({ events, currentDay, onClose }: EventLogPanelProps) {
  const sorted = [...events].sort((a, b) => b.day - a.day);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-400" />
            <h2 className="text-lg font-bold">Planet History</h2>
            <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">
              {events.length} event{events.length !== 1 ? 's' : ''}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {sorted.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Info className="w-8 h-8 mx-auto mb-2 text-slate-600" />
              <p className="text-sm">No events yet. Your planet's story is just beginning.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {sorted.map((evt) => {
                const cfg = SEVERITY_CONFIG[evt.severity];
                const Icon = cfg.icon;
                const isRecent = currentDay - evt.day < 3;
                const effectEntries = getEventEffectEntries(evt);
                return (
                  <div
                    key={evt.eventId}
                    className={`flex items-start gap-3 px-3 py-2.5 rounded-xl border ${cfg.bg} ${cfg.border} ${isRecent ? 'ring-1 ring-white/5' : ''}`}
                  >
                    <div className={`p-1.5 rounded-lg bg-slate-900/50 shrink-0 mt-0.5`}>
                      <Icon className={`w-4 h-4 ${cfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-semibold ${cfg.color}`}>{evt.name}</span>
                        {!evt.resolved && (
                          <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded animate-pulse">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-300 mt-0.5">{evt.message}</p>
                      {effectEntries.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {effectEntries.map((effect) => (
                            <span
                              key={effect.key}
                              className={`rounded px-1.5 py-0.5 text-[10px] font-mono ${
                                effect.value >= 0
                                  ? effect.key === 'pollution'
                                    ? 'bg-amber-950/60 text-amber-300'
                                    : 'bg-emerald-950/60 text-emerald-300'
                                  : effect.key === 'pollution'
                                  ? 'bg-emerald-950/60 text-emerald-300'
                                  : 'bg-red-950/60 text-red-300'
                              }`}
                            >
                              {effect.text}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500">
                        <span>Day {evt.day}</span>
                        <span>{isRecent ? '(recent)' : `(${currentDay - evt.day} days ago)`}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
